import { DashboardLayout } from "../components/layout/DashboardLayout";
import { PageHeader } from "../components/common/PageHeader";
import { useAuthStore } from "../store/authStore";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { User, Mail, Building, Calendar, FileText } from "lucide-react";
import { formatDate } from "../utils/formatters";

export function ProfilePage() {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Please log in to view your profile</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading profile data...</p>
        </div>
      </DashboardLayout>
    );
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Profile"
            description="Manage your account information"
          />

          <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Profile Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage
                      src={user.avatar_url}
                      alt={`${user.first_name} ${user.last_name}`}
                    />
                    <AvatarFallback className="text-lg">
                      {getInitials(user.first_name, user.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <h2 className="text-2xl font-bold">
                      {user.first_name} {user.last_name}
                    </h2>
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {user.email}
                    </p>
                    <Badge variant="secondary" className="w-fit">
                      {user.role}
                    </Badge>
                  </div>
                </div>

                {user.profile_bio && (
                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Bio
                    </h3>
                    <p className="text-muted-foreground">{user.profile_bio}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Account Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Member Since
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(user.createdAt)}
                  </p>
                </div>

                {user.school_id && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      School
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {typeof user.school_id === 'object' ? (user.school_id as unknown as { school_name: string }).school_name : user.school_id}
                    </p>
                  </div>
                )}

                {user.campuses && user.campuses.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Campuses</p>
                    <div className="flex flex-wrap gap-1">
                      {user.campuses.map((campus, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {typeof campus === 'object' ? (campus as unknown as { campus_name: string }).campus_name : campus}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
